(function () {
  const ROOT_SELECTOR = '.hoc-faq';
  const TOPIC_BUTTON_SELECTOR = '.hoc-faq-theme';
  const ITEM_SELECTOR = '.hoc-faq-item';
  const QUESTION_SELECTOR = '.hoc-faq-item__question';
  const ANSWER_LINK_SELECTOR = '.hoc-faq-item__answer a';
  const REACH_LINK_SELECTOR = '.hoc-faq-reach a[href]';
  const ACTIVE_CLASS = 'is-active';

  function isDebugEnabled() {
    return window.HOC_FAQ_DEBUG === true;
  }

  function logDebug(...args) {
    if (isDebugEnabled()) {
      console.log('[FAQ analytics]', ...args);
    }
  }

  function ensureFaqDebugQueue() {
    if (!Array.isArray(window.__HOC_FAQ_EVENTS__)) {
      window.__HOC_FAQ_EVENTS__ = [];
    }
    return window.__HOC_FAQ_EVENTS__;
  }

  function ensureDataLayer() {
    if (!Array.isArray(window.dataLayer)) {
      window.dataLayer = [];
    }
    return window.dataLayer;
  }

  function cleanObject(obj) {
    return Object.fromEntries(
      Object.entries(obj).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    );
  }

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function normalizeFacets(value) {
    return normalizeText(value)
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
      .join(',');
  }

  function parseUrlMeta(href) {
    if (!href) {
      return {
        faq_link_url: '',
        faq_link_type: 'unknown',
        faq_link_domain: '',
      };
    }

    try {
      const url = new URL(href, window.location.origin);
      let type = 'other';

      if (url.protocol === 'mailto:') {
        type = 'email';
      } else if (url.protocol === 'tel:') {
        type = 'phone';
      } else if (url.origin !== window.location.origin) {
        type = 'external';
      } else if (url.pathname.includes('/products/')) {
        type = 'product';
      } else if (url.pathname.includes('/pages/')) {
        type = 'internal_page';
      } else {
        type = 'internal';
      }

      return {
        faq_link_url: url.href,
        faq_link_type: type,
        faq_link_domain: url.hostname,
      };
    } catch (error) {
      return {
        faq_link_url: href,
        faq_link_type: 'unknown',
        faq_link_domain: '',
      };
    }
  }

  function pushFaqEvent(eventName, payload) {
    const basePayload = cleanObject({
      ...payload,
      faq_page_path: window.location.pathname,
      faq_page_title: document.title,
      faq_locale: document.documentElement.lang || '',
      faq_viewport_width: window.innerWidth,
      faq_timestamp: new Date().toISOString(),
    });

    const eventPayload = {
      event: eventName,
      ...basePayload,
    };

    ensureFaqDebugQueue().push(eventPayload);
    ensureDataLayer().push(eventPayload);

    if (typeof window.gtag === 'function' && window.HOC_FAQ_USE_GTAG_FALLBACK === true) {
      window.gtag('event', eventName, basePayload);
    }

    logDebug(eventName, eventPayload);
  }

  function getTopicButtons(root) {
    return Array.from(root.querySelectorAll(TOPIC_BUTTON_SELECTOR));
  }

  function getQuestionItems(root) {
    return Array.from(root.querySelectorAll(ITEM_SELECTOR));
  }

  function getRootContext(root) {
    return {
      faq_section_id: root.dataset.sectionId || '',
      faq_page_handle: root.dataset.faqPageHandle || '',
    };
  }

  function getTopicDataFromButton(button) {
    return {
      faq_topic_key: button.dataset.topicKey || button.dataset.themeFilter || '',
      faq_topic_handle: button.dataset.topicHandle || '',
      faq_topic_label:
        button.dataset.topicLabel ||
        normalizeText(button.querySelector('.hoc-faq-theme__label')?.textContent) ||
        'All topics',
    };
  }

  function getItemData(item) {
    return {
      faq_topic_key: item.dataset.topicKey || item.dataset.theme || '',
      faq_topic_handle: item.dataset.topicHandle || '',
      faq_topic_label: item.dataset.topicLabel || '',
      faq_category_key: item.dataset.categoryKey || '',
      faq_category_handle: item.dataset.categoryHandle || '',
      faq_item_handle: item.dataset.itemHandle || '',
      faq_item_question: normalizeText(item.querySelector(QUESTION_SELECTOR)?.textContent),
      faq_item_facets: normalizeFacets(item.dataset.itemFacets || ''),
    };
  }

  function silentlyCloseItem(item) {
    if (!item.open) return;
    item.dataset.suppressToggleAnalytics = 'true';
    item.open = false;
  }

  function shouldShowItemForTopic(item, topicKey) {
    if (topicKey === 'all') return true;
    return (item.dataset.topicKey || item.dataset.theme || '') === topicKey;
  }

  function updateTopicUI(root, nextTopicKey, trackSelection) {
    const buttons = getTopicButtons(root);
    const items = getQuestionItems(root);

    const nextButton =
      buttons.find((button) => {
        return (button.dataset.topicKey || button.dataset.themeFilter || '') === nextTopicKey;
      }) || buttons[0];

    const nextTopicData = nextButton
      ? getTopicDataFromButton(nextButton)
      : {
          faq_topic_key: 'all',
          faq_topic_handle: 'all',
          faq_topic_label: 'All topics',
        };

    const currentTopicKey = root.dataset.currentTopicKey || '';
    const currentTopicHandle = root.dataset.currentTopicHandle || '';

    buttons.forEach((button) => {
      const buttonTopicKey = button.dataset.topicKey || button.dataset.themeFilter || '';
      const isActive = buttonTopicKey === nextTopicData.faq_topic_key;

      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    items.forEach((item) => {
      const showItem = shouldShowItemForTopic(item, nextTopicData.faq_topic_key);
      item.hidden = !showItem;

      if (!showItem) {
        silentlyCloseItem(item);
      }
    });

    root.dataset.currentTopicKey = nextTopicData.faq_topic_key || '';
    root.dataset.currentTopicHandle = nextTopicData.faq_topic_handle || '';

    if (
      trackSelection &&
      (
        nextTopicData.faq_topic_key !== currentTopicKey ||
        nextTopicData.faq_topic_handle !== currentTopicHandle
      )
    ) {
      pushFaqEvent('faq_topic_selected', {
        ...getRootContext(root),
        ...nextTopicData,
      });
    }
  }

  function bindItemToggleAnalytics(root) {
    getQuestionItems(root).forEach((item) => {
      if (item.dataset.toggleAnalyticsBound === 'true') return;
      item.dataset.toggleAnalyticsBound = 'true';

      item.addEventListener('toggle', function () {
        if (item.dataset.suppressToggleAnalytics === 'true') {
          item.removeAttribute('data-suppress-toggle-analytics');
          return;
        }

        const eventName = item.open ? 'faq_question_opened' : 'faq_question_closed';

        pushFaqEvent(eventName, {
          ...getRootContext(root),
          ...getItemData(item),
        });
      });
    });
  }

  function initFaq(root) {
    if (!root || root.dataset.faqInitialized === 'true') return;
    root.dataset.faqInitialized = 'true';

    const buttons = getTopicButtons(root);
    if (!buttons.length) return;

    bindItemToggleAnalytics(root);

    root.addEventListener('click', function (event) {
      const topicButton = event.target.closest(TOPIC_BUTTON_SELECTOR);
      if (topicButton && root.contains(topicButton)) {
        const selectedTopicKey = topicButton.dataset.topicKey || topicButton.dataset.themeFilter || 'all';
        updateTopicUI(root, selectedTopicKey, true);
        return;
      }

      const answerLink = event.target.closest(ANSWER_LINK_SELECTOR);
      if (answerLink && root.contains(answerLink)) {
        const parentItem = answerLink.closest(ITEM_SELECTOR);

        pushFaqEvent('faq_answer_link_clicked', {
          ...getRootContext(root),
          ...getItemData(parentItem),
          ...parseUrlMeta(answerLink.getAttribute('href')),
        });
        return;
      }

      const reachLink = event.target.closest(REACH_LINK_SELECTOR);
      if (reachLink && root.contains(reachLink)) {
        pushFaqEvent('faq_contact_clicked', {
          ...getRootContext(root),
          ...parseUrlMeta(reachLink.getAttribute('href')),
          faq_contact_target: reachLink.getAttribute('href') || '',
        });
      }
    });

    const initialButton =
      root.querySelector(`${TOPIC_BUTTON_SELECTOR}.${ACTIVE_CLASS}`) ||
      root.querySelector(`${TOPIC_BUTTON_SELECTOR}[data-topic-key="all"]`) ||
      buttons[0];

    const initialTopicKey = initialButton?.dataset?.topicKey || initialButton?.dataset?.themeFilter || 'all';
    updateTopicUI(root, initialTopicKey, false);

    const activeTopicButton = root.querySelector(`${TOPIC_BUTTON_SELECTOR}.${ACTIVE_CLASS}`);
    const initialTopicData = activeTopicButton
      ? getTopicDataFromButton(activeTopicButton)
      : {
          faq_topic_key: 'all',
          faq_topic_handle: 'all',
          faq_topic_label: 'All topics',
        };

    pushFaqEvent('faq_viewed', {
      ...getRootContext(root),
      ...initialTopicData,
      faq_total_topics: getTopicButtons(root).filter((button) => {
        return (button.dataset.topicKey || button.dataset.themeFilter || '') !== 'all';
      }).length,
      faq_total_items: getQuestionItems(root).length,
    });
  }

  function initAllFaq() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllFaq);
  } else {
    initAllFaq();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    event.target.querySelectorAll(ROOT_SELECTOR).forEach(initFaq);
  });

  document.addEventListener('shopify:section:reorder', function () {
    initAllFaq();
  });
})();